import React from "react";
import { motion } from "framer-motion";
import { TrendUp, Bed, Users, CurrencyInr, Bell, CheckCircle, Clock, Heartbeat } from "@phosphor-icons/react";

/* Detailed HTML replicas of Atikxzio HMS screens (no stock imagery). */

const T = {
  en: {
    dash: "Owner's dashboard", today: "Today", opd: "OPD patients", beds: "Beds occupied",
    collection: "Collection", pending: "Pending dues", trend: "Collection · last 7 days",
    occupancy: "Ward occupancy", tokens: "Today's OPD tokens", waiting: "Waiting", inRoom: "In room", done: "Done",
    ward: "Ward board", nurse: "Nurse tasks", vitals: "Vitals due", meds: "Medicine due",
    bill: "Running bill", discharge: "Discharge summary", approve: "Owner approval",
    queue: "OPD queue", reg: "New registration", print: "Print QR slip",
    items: "Bill items", total: "Total payable", upi: "UPI QR · paid",
    float: "Bed 4 · Vitals due in 10 min",
    names: ["Ramesh Yadav", "Sunita Devi", "Mohd. Arif", "Anita Singh", "Dinesh Gupta", "Kavita Mishra"],
    wards: ["General ward", "Private", "ICU", "Day care"],
    beds4: ["Bed 2 · Ramesh Y.", "Bed 4 · Sunita D.", "Bed 7 · Arif M.", "Bed 9 · Anita S."],
    tasks: ["BP + pulse", "Inj. Ceftriaxone", "Input/output chart", "Dressing change"],
    billItems: [["Consultation — Ortho", "600"], ["Bed charges · 3 days", "2,400"], ["Inj. Ceftriaxone ×6", "1,080"], ["X-ray · knee AP/LAT", "750"]],
  },
  hi: {
    dash: "ओनर डैशबोर्ड", today: "आज", opd: "OPD मरीज़", beds: "भरे बेड",
    collection: "कलेक्शन", pending: "बक़ाया", trend: "कलेक्शन · पिछले 7 दिन",
    occupancy: "वार्ड ऑक्युपेंसी", tokens: "आज के OPD टोकन", waiting: "प्रतीक्षा", inRoom: "कमरे में", done: "पूर्ण",
    ward: "वार्ड बोर्ड", nurse: "नर्स टास्क", vitals: "वाइटल्स बाक़ी", meds: "दवा का समय",
    bill: "रनिंग बिल", discharge: "डिस्चार्ज सारांश", approve: "ओनर अप्रूवल",
    queue: "OPD क्यू", reg: "नया रजिस्ट्रेशन", print: "QR पर्ची प्रिंट करें",
    items: "बिल आइटम", total: "कुल देय", upi: "UPI QR · भुगतान",
    float: "बेड 4 · 10 मिनट में वाइटल्स",
    names: ["रमेश यादव", "सुनीता देवी", "मो. आरिफ़", "अनीता सिंह", "दिनेश गुप्ता", "कविता मिश्रा"],
    wards: ["जनरल वार्ड", "प्राइवेट", "ICU", "डे केयर"],
    beds4: ["बेड 2 · रमेश य.", "बेड 4 · सुनीता दे.", "बेड 7 · आरिफ़", "बेड 9 · अनीता सिं."],
    tasks: ["BP + पल्स", "Inj. Ceftriaxone", "इनपुट/आउटपुट चार्ट", "ड्रेसिंग बदलें"],
    billItems: [["कंसल्टेशन — ऑर्थो", "600"], ["बेड चार्ज · 3 दिन", "2,400"], ["Inj. Ceftriaxone ×6", "1,080"], ["एक्स-रे · घुटना", "750"]],
  },
};

const Kpi = ({ icon: Icon, label, value, delta, tone = "teal" }) => (
  <div className="rounded-xl border border-navy/[0.07] bg-white p-3">
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <Icon size={14} weight="duotone" className={tone === "teal" ? "text-teal-ink" : "text-electric-ink"} />
    </div>
    <p className="mt-1.5 font-display text-[17px] font-black leading-none text-navy">{value}</p>
    {delta && <p className="mt-1 text-[10px] font-bold text-teal-ink">{delta}</p>}
  </div>
);

export const MockOwnerDashboard = ({ lang = "en" }) => {
  const t = T[lang];
  const bars = [46, 58, 52, 71, 64, 88, 79];
  return (
    <div className="w-full rounded-xl bg-[#F5F7FB] p-3.5">
      <div className="flex items-center justify-between">
        <p className="font-display text-xs font-bold text-navy">{t.dash}</p>
        <span className="rounded-full bg-teal/10 px-2 py-0.5 text-[10px] font-bold text-teal-ink">{t.today}</span>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        <Kpi icon={Users} label={t.opd} value="128" delta="+12%" />
        <Kpi icon={Bed} label={t.beds} value="18/24" tone="blue" />
        <Kpi icon={CurrencyInr} label={t.collection} value="₹2,45,600" delta="+8%" />
        <Kpi icon={TrendUp} label={t.pending} value="₹38,150" tone="blue" />
      </div>

      <div className="mt-2.5 grid gap-2.5 sm:grid-cols-5">
        <div className="rounded-xl border border-navy/[0.07] bg-white p-3 sm:col-span-3">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{t.trend}</p>
          <div className="mt-3 flex h-20 items-end gap-1.5">
            {bars.map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                whileInView={{ height: `${h}%` }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className={`flex-1 rounded-t ${i === 6 ? "bg-teal" : "bg-teal/30"}`}
              />
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-navy/[0.07] bg-white p-3 sm:col-span-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{t.occupancy}</p>
          <div className="mt-3 space-y-2">
            {[["0", 82], ["1", 60], ["2", 95], ["3", 40]].map(([k, w]) => (
              <div key={k}>
                <div className="flex justify-between text-[10px] font-semibold text-navy/70">
                  <span>{t.wards[Number(k)]}</span><span>{w}%</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-navy/[0.07]">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: `${w}%` }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} className="h-full rounded-full bg-electric" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-2.5 rounded-xl border border-navy/[0.07] bg-white p-3">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{t.tokens}</p>
        <div className="mt-2 divide-y divide-navy/[0.06]">
          {t.names.slice(0, 4).map((n, i) => (
            <div key={n} className="flex items-center justify-between py-1.5">
              <span className="flex items-center gap-2 text-[11px] font-semibold text-navy">
                <span className="flex h-5 w-5 items-center justify-center rounded-md bg-navy/[0.06] font-display text-[10px] font-bold text-navy">{12 + i}</span>
                {n}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                i === 0 ? "bg-teal/10 text-teal-ink" : i === 1 ? "bg-electric/10 text-electric-ink" : "bg-navy/[0.08] text-navy/80"
              }`}>
                {i === 0 ? t.inRoom : i === 1 ? t.waiting : t.done}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MockOpdQueue = ({ lang = "en" }) => {
  const t = T[lang];
  return (
    <div className="w-full rounded-xl bg-[#F5F7FB] p-3.5">
      <div className="flex items-center justify-between">
        <p className="font-display text-xs font-bold text-navy">{t.queue}</p>
        <span className="rounded-full bg-electric-ink px-2.5 py-1 text-[10px] font-bold text-white">{t.reg}</span>
      </div>
      <div className="mt-3 space-y-2">
        {t.names.map((n, i) => (
          <div key={n} className="flex items-center justify-between rounded-xl border border-navy/[0.07] bg-white px-3 py-2.5">
            <span className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-navy text-[11px] font-black text-white">{10 + i}</span>
              <span>
                <span className="block text-[11px] font-bold text-navy">{n}</span>
                <span className="block text-[10px] text-slate-500">OPD · {["Ortho", "Medicine", "Ortho", "Neuro", "Medicine", "Ortho"][i]}</span>
              </span>
            </span>
            {i === 0
              ? <span className="rounded-full bg-teal/10 px-2 py-0.5 text-[9px] font-bold uppercase text-teal-ink">{t.inRoom}</span>
              : <Clock size={14} className="text-slate-400" />}
          </div>
        ))}
      </div>
      <button className="mt-3 w-full rounded-xl bg-navy py-2.5 text-[11px] font-bold text-white">{t.print}</button>
    </div>
  );
};

export const MockBilling = ({ lang = "en" }) => {
  const t = T[lang];
  return (
    <div className="w-full rounded-xl bg-[#F5F7FB] p-3.5">
      <div className="flex items-center justify-between">
        <p className="font-display text-xs font-bold text-navy">{t.bill}</p>
        <span className="rounded-full bg-teal/10 px-2 py-0.5 text-[10px] font-bold text-teal-ink">{t.upi}</span>
      </div>
      <div className="mt-3 rounded-xl border border-navy/[0.07] bg-white p-3">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{t.items}</p>
        <div className="mt-2 divide-y divide-navy/[0.06]">
          {t.billItems.map(([label, amt]) => (
            <div key={label} className="flex items-center justify-between py-1.5 text-[11px]">
              <span className="font-semibold text-navy">{label}</span>
              <span className="font-display font-bold text-navy">₹{amt}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-between border-t border-navy/10 pt-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{t.total}</span>
          <span className="font-display text-base font-black text-navy">₹4,830</span>
        </div>
      </div>
      <div className="mt-2.5 flex items-center justify-between rounded-xl border border-navy/[0.07] bg-white px-3 py-2.5">
        <span className="text-[11px] font-semibold text-navy">{t.approve}</span>
        <span className="flex items-center gap-1.5 text-[10px] font-bold text-teal-ink"><CheckCircle size={13} weight="fill" /> OK</span>
      </div>
    </div>
  );
};

export const MockWardPhone = ({ lang = "en", className = "" }) => {
  const t = T[lang];
  return (
    <div className={`w-[176px] rounded-[1.6rem] border-[6px] border-navy-deep bg-navy-deep shadow-[0_18px_40px_rgba(5,11,20,0.45)] ${className}`}>
      <div className="overflow-hidden rounded-[1.1rem] bg-white">
        <div className="flex items-center justify-between bg-navy px-3 py-2">
          <span className="font-display text-[10px] font-bold text-white">{t.ward}</span>
          <Bell size={12} weight="fill" className="text-teal" />
        </div>
        <div className="space-y-1.5 p-2.5">
          {t.beds4.map((b, i) => (
            <div key={b} className={`rounded-lg border px-2 py-1.5 ${i === 1 ? "border-teal/40 bg-teal/[0.07]" : "border-navy/[0.07] bg-white"}`}>
              <p className="text-[10px] font-bold text-navy">{b}</p>
              <p className="mt-0.5 flex items-center gap-1 text-[9px] text-slate-500">
                <Heartbeat size={10} weight="fill" className={i === 1 ? "text-teal-ink" : "text-slate-400"} />
                {t.tasks[i]}
              </p>
            </div>
          ))}
          <div className="rounded-lg bg-navy px-2 py-1.5 text-center text-[9px] font-bold text-white">{t.nurse}</div>
        </div>
      </div>
    </div>
  );
};

export const BrowserFrame = ({ children, label = "atikxzio.com" }) => (
  <div className="w-full rounded-2xl border border-white/15 bg-white/[0.07] p-2 shadow-[0_28px_70px_rgba(5,11,20,0.5)] backdrop-blur-sm">
    <div className="flex items-center gap-2 px-2 pb-2 pt-1">
      {["#F87171", "#FBBF24", "#34D399"].map((c) => (<span key={c} className="h-2 w-2 rounded-full" style={{ background: c }} />))}
      <span className="ml-2 flex-1 truncate rounded-md bg-white/10 px-2 py-0.5 text-[9px] font-semibold text-white/60">{label}</span>
    </div>
    <div className="overflow-hidden rounded-xl bg-white p-1.5">{children}</div>
  </div>
);

export const SCREEN_CAPTION = {
  en: "Real screens from our demo hospital. Patient names are fictional.",
  hi: "हमारे डेमो अस्पताल की असली स्क्रीन। मरीज़ों के नाम काल्पनिक हैं।",
};

/* Real product screenshot inside a browser frame. Swap `src` to change the screen. */
export const ScreenShot = ({ src, alt, label = "atikxzio.com", lang = "en", caption = true, priority = false }) => (
  <figure className="w-full">
    <div className="w-full rounded-2xl border border-white/15 bg-white/[0.07] p-2 shadow-[0_28px_70px_rgba(5,11,20,0.5)] backdrop-blur-sm">
      <div className="flex items-center gap-2 px-2 pb-2 pt-1">
        {["#F87171", "#FBBF24", "#34D399"].map((c) => (<span key={c} className="h-2 w-2 rounded-full" style={{ background: c }} />))}
        <span className="ml-2 flex-1 truncate rounded-md bg-white/10 px-2 py-0.5 text-[9px] font-semibold text-white/60">{label}</span>
      </div>
      <img
        src={src}
        alt={alt}
        width="1600"
        height="940"
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className="w-full rounded-xl bg-white"
      />
    </div>
    {caption && <figcaption className="mt-3 text-[11px] text-white/50">{SCREEN_CAPTION[lang]}</figcaption>}
  </figure>
);

export const PhoneShot = ({ src, alt, className = "", lang = "en" }) => (
  <div className={`w-[176px] overflow-hidden rounded-[1.6rem] border-[6px] border-navy-deep bg-navy-deep shadow-[0_18px_40px_rgba(5,11,20,0.45)] ${className}`}>
    <img src={src} alt={alt} width="440" height="621" loading="lazy" decoding="async" className="w-full rounded-[1.1rem] bg-white object-cover object-top" />
  </div>
);

export const HeroProductStack = ({ lang = "en" }) => {
  const t = T[lang];
  return (
    <div className="relative pb-10 pl-0 sm:pl-6" data-testid="hero-product-stack">
      <motion.div
        animate={{ y: [0, -7, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="motion-reduce:animate-none"
      >
        <ScreenShot
          src="/screens/owner-dashboard.webp"
          alt="Atikxzio HMS owner's dashboard showing money collected, billed revenue, bed occupancy, dues and the daily money trend"
          label="atikxzio.com/dashboard"
          lang={lang}
          priority
          caption={false}
        />
      </motion.div>

      <motion.div
        animate={{ y: [0, 7, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
        className="absolute -bottom-6 -left-5 hidden origin-bottom-left scale-90 sm:block motion-reduce:animate-none"
      >
        <PhoneShot src="/screens/ward-phone.webp" alt="Atikxzio HMS bed board on a phone, showing occupied and free beds by ward" lang={lang} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute -right-3 bottom-12 hidden items-center gap-2 rounded-xl border border-navy/10 bg-white px-3 py-2 shadow-[0_14px_30px_rgba(5,11,20,0.25)] lg:flex"
        data-testid="hero-float-card"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal/10 text-teal-ink"><Bell size={14} weight="fill" /></span>
        <span className="text-[11px] font-bold text-navy">{t.float}</span>
      </motion.div>

      <p className="mt-6 max-w-sm text-[11px] text-white/50 sm:mt-8 sm:pl-44">{SCREEN_CAPTION[lang]}</p>
    </div>
  );
};

/* Kept as a fallback: the full HTML replica stack used before real screenshots existed. */
export const HeroProductStackReplica = ({ lang = "en" }) => {
  const t = T[lang];
  return (
    <div className="relative pb-10 pl-0 sm:pl-6" data-testid="hero-product-stack-replica">
      <BrowserFrame label="atikxzio.com/dashboard"><MockOwnerDashboard lang={lang} /></BrowserFrame>
      <div className="absolute -bottom-6 -left-5 hidden origin-bottom-left scale-90 sm:block"><MockWardPhone lang={lang} /></div>
      <span className="sr-only">{t.float}</span>
    </div>
  );
};
